/* aa-cart.js — the cart drawer (PLAN.md PG0, Figma 36:2289)
 *
 * Replaces Dawn's cart.js / cart-drawer.js for this drawer. Dawn's version is coupled by id and
 * class to its own table markup (`#Quantity-<line>`, `.cart-item`, `.drawer__inner`), so it could
 * not drive this layout without dragging those names through it.
 *
 * Two rules shape everything below.
 *
 * 1. **Liquid renders every amount.** A quantity change asks `/cart/change.js` to re-render the
 *    drawer section and hand the markup back in the SAME response, so prices, the free-shipping
 *    threshold and the total all come back formatted by Shopify. Nothing here does money
 *    arithmetic — PLN and EUR are both presented (M-004) and a hand-formatted figure would be
 *    wrong in one of them.
 *
 * 2. **Every listener is delegated to the drawer root.** The panel's whole contents are replaced
 *    on each change, so anything bound to a button inside it would be bound to a corpse. The root
 *    element survives; the markup under it does not.
 *
 * Lines are addressed by `item.key`, never by index: removing a line renumbers everything after
 * it, so an index captured at render time points at the wrong product by the time it is used.
 */
(function () {
  'use strict';

  const root = document.querySelector('[data-aa-cart-drawer]');
  if (!root) return;

  const SECTION_ID = 'aa-cart-drawer';

  /* ==========================================================================
     1. Open / close
     ========================================================================== */

  let lastFocus = null;

  const focusables = () =>
    Array.prototype.filter.call(
      root.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'),
      (el) => el.offsetParent !== null
    );

  function open(trigger) {
    if (trigger) lastFocus = trigger;
    root.dataset.open = 'true';
    document.body.classList.add('overflow-hidden');
    const panel = root.querySelector('.aa-cart__panel');
    if (panel) panel.focus();
    fillUpsell();
  }

  function close() {
    delete root.dataset.open;
    document.body.classList.remove('overflow-hidden');
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
    lastFocus = null;
  }

  const isOpen = () => root.dataset.open === 'true';

  document.addEventListener('keydown', (event) => {
    if (!isOpen()) return;
    if (event.key === 'Escape') {
      close();
      return;
    }
    if (event.key !== 'Tab') return;

    // The drawer is modal, so focus must not walk out of it into the page behind.
    const items = focusables();
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    } else if (!root.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    }
  });

  /* The header's cart icon is a real link to /cart, and stays one: it must still work before this
     script runs, and with JavaScript off. Opening the drawer is layered on top of it. */
  const cartLink = document.getElementById('cart-icon-bubble');
  if (cartLink) {
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      open(cartLink);
    });
  }

  /* ==========================================================================
     2. Cart mutations
     ========================================================================== */

  const changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change';
  const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

  /* Replaces the panel's contents with freshly rendered Liquid. The section arrives wrapped in
     Shopify's own section div, so the drawer element is dug out of it rather than used whole —
     replacing the root would drop every delegated listener above. */
  function applySection(html) {
    if (!html) return;
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-aa-cart-drawer]');
    if (!next) return;
    const wasOpen = isOpen();
    root.innerHTML = next.innerHTML;
    if (wasOpen) root.dataset.open = 'true';
    syncHeaderCount();
    mirrorAnnouncement();
    fillUpsell();
    startCountdown();
  }

  /* The drawer's own badge comes back with the re-rendered section; the header's does not, and
     re-rendering the whole header section to update one number would fetch the menus with it.

     The count is read off the drawer Liquid just rendered rather than out of the response body,
     because `/cart/add.js` answers with the LINES IT ADDED, not with the cart — so an upsell add
     left the header showing the old number while the drawer beside it showed the new one. */
  function syncHeaderCount() {
    const badge = document.querySelector('.aa-header__cart-count');
    if (!badge) return;
    const source = root.querySelector('[data-aa-cart-count]');
    const value = source ? source.textContent.trim() : '';
    badge.textContent = value;
    badge.hidden = value === '';
  }

  /* `busy` is the control the shopper actually pressed: it wears the spinner. The drawer as a
     whole goes inert at the same time, so a second change cannot be fired against markup that is
     about to be replaced by the first one's response. */
  function post(url, body, busy) {
    if (busy) busy.setAttribute('aria-busy', 'true');
    root.dataset.busy = 'true';
    return fetch(url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(
        Object.assign({ sections: SECTION_ID, sections_url: window.location.pathname }, body)
      ),
    })
      .then((response) => response.json())
      .then((state) => {
        if (state.status) throw new Error(state.description || state.message);
        if (state.sections) applySection(state.sections[SECTION_ID]);
        if (typeof window.publish === 'function') {
          window.publish('cart-update', { source: 'aa-cart' });
        }
        return state;
      })
      .catch(() => {
        // A failed change must not leave a row dimmed and inert with a stale number in it.
        refresh();
      })
      .finally(() => {
        delete root.dataset.busy;
        // Usually moot — a success replaces this element outright — but a failure leaves the
        // original control in place, and it must not be left spinning forever.
        if (busy) busy.removeAttribute('aria-busy');
      });
  }

  function refresh() {
    return fetch(`${window.location.pathname}?section_id=${SECTION_ID}`)
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then(applySection)
      .catch(() => {
        /* Stale figures beat a half-erased drawer. */
      });
  }

  function changeLine(line, quantity, busy) {
    const key = line.dataset.key;
    if (!key) return;
    line.setAttribute('aria-busy', 'true');
    post(changeUrl, { id: key, quantity: quantity }, busy);
  }

  root.addEventListener('click', (event) => {
    if (event.target.closest('[data-aa-cart-close]')) {
      close();
      return;
    }

    const step = event.target.closest('[data-aa-cart-step]');
    if (step) {
      const line = step.closest('[data-aa-cart-line]');
      const input = line && line.querySelector('[data-aa-cart-input]');
      if (!line || !input) return;
      const next = Math.max(0, (Number(input.value) || 0) + Number(step.dataset.aaCartStep));
      changeLine(line, next, step);
      return;
    }

    const remove = event.target.closest('[data-aa-cart-remove]');
    if (remove) {
      const line = remove.closest('[data-aa-cart-line]');
      if (line) changeLine(line, 0, remove);
      return;
    }

    const add = event.target.closest('[data-aa-cart-add]');
    if (add) {
      post(addUrl, { items: [{ id: Number(add.dataset.variantId), quantity: 1 }] }, add);
    }
  });

  /* Typing straight into the field. `change`, not `input` — a request per keystroke would race
     itself, and 12 typed as 1 then 12 would briefly set the line to 1. */
  root.addEventListener('change', (event) => {
    const input = event.target.closest('[data-aa-cart-input]');
    if (!input) return;
    const line = input.closest('[data-aa-cart-line]');
    // No button was pressed, so the stepper as a whole wears the spinner.
    if (line) changeLine(line, Math.max(0, Number(input.value) || 0), input.closest('[data-aa-cart-qty]'));
  });

  /* Checkout is a full navigation, not a fetch, so nothing here clears the spinner — the page
     goes away. That is the point: on a slow connection the button otherwise sits unpressed for a
     second and gets clicked again. */
  root.addEventListener('submit', (event) => {
    const form = event.target.closest('form');
    const button = form && form.querySelector('[data-aa-cart-checkout]');
    if (button) button.setAttribute('aria-busy', 'true');
  });

  /* Anything else that touches the cart — the product page's add-to-cart, a quick add from a card
     — publishes this, and the drawer may be showing figures from before it. */
  if (typeof window.subscribe === 'function') {
    window.subscribe('cart-update', (payload) => {
      if (payload && payload.source === 'aa-cart') return;
      refresh();
    });
  }

  /* ==========================================================================
     3. Upsell
     ========================================================================== */

  /* Shopify computes recommendations asynchronously, so they are not in the first render. The
     shell carries its own URL and is filled once — `data-aa-bound` rather than a check on the
     children, because an anchor product with no recommendations legitimately comes back empty and
     must not be re-fetched on every open. */
  function fillUpsell() {
    const shell = root.querySelector('aa-cart-upsell[data-url]');
    if (!shell || shell.dataset.aaBound === 'true') return;
    shell.dataset.aaBound = 'true';

    fetch(shell.dataset.url)
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then((html) => {
        const next = new DOMParser().parseFromString(html, 'text/html').querySelector('aa-cart-upsell');
        if (next) shell.innerHTML = next.innerHTML;
      })
      .catch(() => {
        /* An empty upsell is the correct outcome for a product with no recommendations. */
      });
  }

  /* ==========================================================================
     4. Same-day dispatch countdown
     ========================================================================== */

  const WAREHOUSE_TZ = 'Europe/Warsaw';

  /* The cut-off is a warehouse wall clock (D6), not the visitor's, and Liquid's `now` is UTC and
     would be frozen into the page cache. Same reasoning, and the same helper, as the product
     page's delivery estimate. */
  function warehouseNow() {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: WAREHOUSE_TZ,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    const get = (type) => parts.find((part) => part.type === type).value;
    return {
      weekday: get('weekday'),
      seconds: Number(get('hour')) * 3600 + Number(get('minute')) * 60 + Number(get('second')),
    };
  }

  let timer = null;

  function startCountdown() {
    if (timer) clearInterval(timer);

    const band = root.querySelector('[data-aa-cart-dispatch]');
    if (!band) return;

    const [hours, minutes] = String(band.dataset.cutoff || '14:00').split(':').map(Number);
    const cutoff = (hours || 0) * 3600 + (minutes || 0) * 60;

    const tick = () => {
      const now = warehouseNow();
      // Working days only (D6a): a Saturday order does not ship today whatever the clock says.
      const weekend = now.weekday === 'Sat' || now.weekday === 'Sun';
      const left = cutoff - now.seconds;

      if (weekend || left <= 0) {
        band.hidden = true;
        if (timer) clearInterval(timer);
        return;
      }

      band.hidden = false;
      const pad = (value) => String(value).padStart(2, '0');
      const units = { h: Math.floor(left / 3600), m: Math.floor((left % 3600) / 60), s: left % 60 };
      band.querySelectorAll('[data-unit]').forEach((el) => {
        el.textContent = pad(units[el.dataset.unit]);
      });
    };

    tick();
    timer = setInterval(tick, 1000);
  }

  startCountdown();
  if (isOpen()) fillUpsell();

  /* ==========================================================================
     5. The interface Dawn's product-form.js expects
     ========================================================================== */

  /* The product page's own add-to-cart is still Dawn's `product-form.js`, and it finds the cart by
     tag name — `document.querySelector('cart-notification') || document.querySelector('cart-drawer')`
     — falling back to `window.location = routes.cart_url` when neither exists. Removing Dawn's
     drawer therefore turned the buy button into a link to /cart. Implementing the three methods it
     calls turns it back into a drawer, and costs three lines.

     Dawn's own `cart-drawer.js` is not loaded, so nothing else defines this name. */
  if (!customElements.get('cart-drawer')) {
    customElements.define(
      'cart-drawer',
      class extends HTMLElement {
        getSectionsToRender() {
          return [{ id: SECTION_ID }];
        }

        setActiveElement(element) {
          lastFocus = element;
        }

        renderContents(state) {
          if (state && state.sections) applySection(state.sections[SECTION_ID]);
          open();
        }

        open(trigger) {
          open(trigger);
        }

        close() {
          close();
        }
      }
    );
  }

  /* The drawer's strip renders the translated default, which is what the header's own strip falls
     back to. A merchant who types a message into the header section would otherwise see it
     disappear when the cart opens, so the live one wins where it exists. */
  function mirrorAnnouncement() {
    const mine = root.querySelector('[data-aa-cart-announcement]');
    const live = document.querySelector('.aa-announcement__text');
    if (mine && live && live.textContent.trim()) mine.textContent = live.textContent.trim();
  }

  mirrorAnnouncement();

  /* For our own add-to-cart paths in aa-product.js, which have their own fetch. */
  window.AACart = { open: open, close: close, refresh: refresh };
})();
